import { useQuery } from '@tanstack/react-query';
import { getSizeLabel } from '@util/createListingHelpers';
import { getBidsCountByProductId } from '@util/firestore/bid/bid.service';
import { cn } from '@util/index';
import { isVehicleCategory, type ProductLight } from 'models/product';

export const ProductTitle = ({
  product,
  whiteText = false,
  isFavoritesPage = false,
}: {
  product: ProductLight;
  whiteText?: boolean;
  isFavoritesPage?: boolean;
}) => {
  const { data: bidCount } = useQuery({
    queryKey: ['bidCount', product.id],
    queryFn: () => getBidsCountByProductId(product.id),
    enabled: !!product.is_auction,
  });

  const parsedLocation = product.location_display?.trim().replace(', USA', '');

  const isWarehouseProduct = product.integration_info?.type === 'warehouse';

  const hasSizes =
    !isWarehouseProduct &&
    (!!product.size?.letter ||
      !!product.size?.number ||
      product.has_variations);

  return (
    <div className="flex flex-col gap-4">
      <span
        className={cn(
          'text-[1.6rem] font-medium leading-tight sm:text-[1.9rem]',
          isFavoritesPage
            ? 'line-clamp-3 h-[3.8rem] overflow-hidden sm:h-[5rem]'
            : 'line-clamp-3 overflow-hidden text-ellipsis',
          whiteText ? 'text-white' : ''
        )}
        style={
          isFavoritesPage
            ? {
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }
            : undefined
        }
      >
        {product.title}
      </span>

      {product.is_auction && (
        <div className="flex w-full items-center">
          <div className="flex w-full items-center gap-4">
            <span
              className={cn(
                'text-[1.6rem] sm:text-[1.8rem]',
                whiteText ? 'text-zinc-300' : 'text-zinc-500'
              )}
            >
              Auction
            </span>

            <div
              className={cn(
                'h-2 w-2 rounded-full',
                whiteText ? 'bg-zinc-300' : 'bg-zinc-400'
              )}
            />

            <span
              className={cn(
                'whitespace-nowrap text-[1.6rem] sm:text-[1.8rem]',
                whiteText ? 'text-gray-300' : 'text-gray-700'
              )}
            >
              {bidCount !== undefined ? `${bidCount} Bids` : ''}
            </span>
          </div>
        </div>
      )}

      {!product.is_auction && (
        <div className="mt-2 flex w-full items-center">
          {isVehicleCategory(product.category) ? (
            <div className="flex w-full min-w-0 items-center gap-4">
              <span
                className={cn(
                  'shrink-0 text-[1.6rem] sm:text-[1.8rem]',
                  whiteText ? 'text-zinc-300' : 'text-zinc-500'
                )}
              >
                {product.condition}
              </span>

              <div
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full',
                  whiteText ? 'bg-zinc-300' : 'bg-zinc-400'
                )}
              />

              <span
                className={cn(
                  'min-w-0 truncate text-[1.6rem] sm:text-[1.8rem]',
                  whiteText ? 'text-zinc-300' : 'text-zinc-500'
                )}
              >
                {parsedLocation}
              </span>
            </div>
          ) : (
            <div className="flex w-full items-center gap-4">
              <span
                className={cn(
                  'text-[1.6rem] sm:text-[1.8rem]',
                  whiteText ? 'text-zinc-300' : 'text-zinc-500'
                )}
              >
                {product.condition}
              </span>

              {hasSizes && (
                <div
                  className={cn(
                    'h-2 w-2 rounded-full',
                    whiteText ? 'bg-zinc-300' : 'bg-zinc-400'
                  )}
                />
              )}

              {hasSizes ? (
                <span
                  className={cn(
                    'whitespace-nowrap text-[1.6rem] sm:text-[1.8rem]',
                    whiteText ? 'text-gray-300' : 'text-gray-500'
                  )}
                >
                  {product.size?.letter && !product.has_variations
                    ? getSizeLabel(product.size.letter)
                    : ''}{' '}
                  {product.size?.number && !product.has_variations
                    ? `${product.size?.letter ? '/ ' : ''} Size ${
                        product.size?.number
                      }`
                    : ''}
                  {product.has_variations ? 'Multiple sizes' : ''}
                </span>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
